<template>
  <div>
    <el-tabs v-model="activeTab" :stretch="true">
      <el-tab-pane
        :label="$t('ProjectSettings_Inbox_Tab_Settings')"
        name="settings"
      >
        <rest-form-item
          prop="inbox_settings.position"
          :label="$t('ProjectSettings_Inbox_Position')"
        >
          <radio-group v-model="model.inbox_settings.position" inline>
            <radio
              label="left"
              :title="$t('ProjectSettings_Inbox_Position_Left')"
              :description="$t('ProjectSettings_Inbox_Position_Left_Hint')"
            />
            <radio
              label="right"
              :title="$t('ProjectSettings_Inbox_Position_Right')"
              :description="$t('ProjectSettings_Inbox_Position_Right_Hint')"
            />
          </radio-group>
        </rest-form-item>

        <rest-form-item
          v-if="type === 'web'"
          prop="inbox_settings.layout"
          :label="$t('ProjectSettings_Inbox_Layout')"
        >
          <radio-group v-model="model.inbox_settings.layout">
            <radio
              label="panel"
              :title="$t('ProjectSettings_Inbox_Layout_Side_Panel')"
              :description="$t('ProjectSettings_Inbox_Layout_Side_Panel_Hint')"
            />
            <radio
              label="popup"
              :title="$t('ProjectSettings_Inbox_Layout_Pop_Up')"
              :description="$t('ProjectSettings_Inbox_Layout_Pop_Up_Hint')"
            />
          </radio-group>
        </rest-form-item>

        <rest-form-item
          v-if="type === 'web'"
          prop="inbox_settings.button"
          :label="$t('ProjectSettings_Inbox_Button')"
        >
          <radio-group v-model="model.inbox_settings.button">
            <radio
              label="default"
              :title="$t('ProjectSettings_Inbox_Button_Default')"
              :description="$t('ProjectSettings_Inbox_Button_Default_Hint')"
            />

            <radio
              label="custom"
              :title="$t('ProjectSettings_Inbox_Button_Custom')"
              :description="$t('ProjectSettings_Inbox_Button_Custom_Hint')"
            />
          </radio-group>
        </rest-form-item>

        <rest-form-item
          prop="inbox_settings.switch"
          :label="$t('ProjectSettings_Inbox_Push_Subscription_Switch')"
          :description="
            $t('ProjectSettings_Inbox_Push_Subscription_Switch_Hint')
          "
        >
          <toggle v-model="model.inbox_settings.switch" />
        </rest-form-item>
      </el-tab-pane>

      <el-tab-pane
        :label="$t('ProjectSettings_Inbox_Tab_Styles')"
        name="styles"
      >
        <rest-form-item
          prop="inbox_settings.color"
          :label="$t('ProjectSettings_Inbox_Style')"
        >
          <radio-group v-model="model.inbox_settings.color" inline>
            <radio
              label="light"
              :title="$t('ProjectSettings_Inbox_Style_Light')"
            />
            <radio
              label="dark"
              :title="$t('ProjectSettings_Inbox_Style_Dark')"
            />
            <radio
              label="custom"
              :title="$t('ProjectSettings_Inbox_Style_Custom')"
            />
          </radio-group>
        </rest-form-item>

        <el-row>
          <el-col>
            <div
              class="picker-wrap"
              v-for="color_option in color_options"
              :key="color_option.field"
            >
              <color-picker-field
                v-if="model.inbox_settings.colors"
                v-model="model.inbox_settings.colors[color_option.field]"
                :read-only="isPickersDisable"
                :label-text="$t(color_option.title)"
              />
            </div>
          </el-col>
        </el-row>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import ColorPickerField from '@/common/components/form/ColorPickerField'
import RestFormItem from '@/common/components/rest/RestFormItem'
import Radio from '@/common/components/form/Radio'
import RadioGroup from '@/common/components/form/RadioGroup'
import Toggle from '@/common/components/form/Toggle'

var allTypes = ['ios', 'android', 'web']
export default {
  name: 'InboxMessageSettingsForm',
  components: {
    Toggle,
    RadioGroup,
    Radio,
    RestFormItem,
    ColorPickerField
  },
  props: {
    type: {
      type: String,
      required: true,
      validator: value => {
        return allTypes.indexOf(value) !== -1
      }
    },
    value: {
      type: Object
    }
  },
  mounted() {
    this.model = this.value
  },

  computed: {
    title() {
      switch (this.type) {
        case 'web':
          return this.$t('ProjectSettings_Inbox_Settings_Web_Title')
        case 'ios':
          return this.$t('ProjectSettings_Inbox_Settings_Ios_Title')
        case 'android':
          return this.$t('ProjectSettings_Inbox_Settings_Android_Title')
        default:
          return ''
      }
    },

    description() {
      switch (this.type) {
        case 'web':
          return this.$t('ProjectSettings_Inbox_Settings_Web_Description')
        case 'ios':
          return this.$t('ProjectSettings_Inbox_Settings_Ios_Description')
        case 'android':
          return this.$t('ProjectSettings_Inbox_Settings_Android_Description')
        default:
          return ''
      }
    },

    isPickersDisable() {
      return this.model.inbox_settings.color !== 'custom'
    }
  },
  data() {
    return {
      activeTab: 'settings',
      color_options: [
        {
          field: 'inbox_background_color',
          title: 'ProjectSettings_Inbox_Background_Colour'
        },
        {
          field: 'inbox_border_color',
          title: 'ProjectSettings_Inbox_Border_Colour'
        },
        {
          field: 'inbox_separator_color',
          title: 'ProjectSettings_Inbox_Separator_Colour'
        },
        {
          field: 'header_background_color',
          title: 'ProjectSettings_Inbox_Header_Background'
        },
        {
          field: 'header_border_color',
          title: 'ProjectSettings_Inbox_Header_Border'
        },
        {
          field: 'header_icon_color',
          title: 'ProjectSettings_Inbox_Header_Icon'
        },
        {
          field: 'header_text_color',
          title: 'ProjectSettings_Inbox_Header_Text'
        },
        {
          field: 'switch_on_color',
          title: 'ProjectSettings_Inbox_Switch_On'
        },
        {
          field: 'switch_off_color',
          title: 'ProjectSettings_Inbox_Switch_Off'
        },
        {
          field: 'notification_title_color',
          title: 'ProjectSettings_Inbox_Notification_Title'
        },
        {
          field: 'notification_text_color',
          title: 'ProjectSettings_Inbox_Notification_Text'
        },
        {
          field: 'notification_date_color',
          title: 'ProjectSettings_Inbox_Notification_Date'
        }
      ],
      static_colors: {
        light: {
          inbox_background_color: '#FFFFFF',
          inbox_border_color: '#888888',
          inbox_separator_color: '#F3F3F3',
          header_background_color: '#FFFFFF',
          header_border_color: '#D9D9D9',
          header_icon_color: '#666666',
          header_text_color: '#666666',
          switch_on_color: '#2fa9d6',
          switch_off_color: '#9e9e9e',
          notification_title_color: '#666666',
          notification_text_color: '#666666',
          notification_date_color: '#B7B7B7'
        },
        dark: {
          inbox_background_color: '#555555',
          inbox_border_color: '#888888',
          inbox_separator_color: '#808080',
          header_background_color: '#666666',
          header_border_color: '#D9D9D9',
          header_icon_color: '#FFFFFF',
          header_text_color: '#FFFFFF',
          switch_on_color: '#2fa9d6',
          switch_off_color: '#9e9e9e',
          notification_title_color: '#FFFFFF',
          notification_text_color: '#FFFFFF',
          notification_date_color: '#B7B7B7'
        }
      },
      model: {
        inbox_settings: {}
      }
    }
  },
  watch: {
    'model.inbox_settings.color': {
      handler(val) {
        if (this.static_colors.hasOwnProperty(val)) {
          Object.assign(
            this.model.inbox_settings.colors,
            this.static_colors[val]
          )
        } else if (
          val === 'custom' &&
          this.model.inbox_settings.custom_colors
        ) {
          this.model.inbox_settings.colors = Object.assign(
            {},
            this.model.inbox_settings.custom_colors
          )
        }
      }
    },
    'model.inbox_settings.colors': {
      handler(val) {
        if (this.model.inbox_settings.color === 'custom') {
          this.model.custom_colors = Object.assign({}, this.model.colors)
        }
      },
      deep: true
    },
    'model.inbox_settings': {
      handler(val) {
        this.model[`inbox_${this.type}_settings`] = Object.assign(
          this.model.inbox_settings,
          val
        )
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.picker-wrap {
  float: left;
  margin-right: 15px;
}
</style>



// WEBPACK FOOTER //
// src/modules/settings/components/InboxMessageSettingsForm.vue